/* Duyduğunu Çal: hear a melody, play it back on the guitar. */
import { type AudioEvent, audio, type ChordInput } from '../../core/audio';
import { award, getSettings } from '../../core/store';
import { chordSymbol, keyFlat, keyName, type Mode, noteName, pcName } from '../../core/theory';
import { choice, type Rng } from '../../core/util';
import type { ExerciseConfig, NotesQuestion } from '../exercise/types';
import type { ChallengeDef, LevelDef } from '../levels';
import { contour, melody } from './melody';

interface MelodyOpts {
  info?: string;
  flex?: boolean;
  fb?: { from: number; to: number };
  flat?: boolean;
  bpm?: number;
  keyChord?: ChordInput | null;
  maxMiss?: number;
  points?: number;
  review?: string;
}

/** Plays the melody, optionally after the key chord. */
function melodyPlayer(target: number[], o: MelodyOpts) {
  const ev: AudioEvent[] = [];
  let start = 0;
  if (o.keyChord && getSettings().showKeyHint) {
    ev.push(...audio.chordEvents(o.keyChord, 0, 1.15, { vel: 0.6 }));
    start = 1.45;
  }
  const s = audio.seqEvents(target, { bpm: o.bpm || 92, start, inst: 'guitar', legato: 0.9 });
  ev.push(...s.ev);
  return audio.play(ev, { length: s.length });
}

export function notesQ(target: number[], o: MelodyOpts): NotesQuestion {
  return {
    type: 'notes',
    target,
    prompt: target.length === 1 ? 'Duyduğun notayı gitarda çal' : `Duyduğun ${target.length} notayı sırayla çal`,
    info: o.info,
    flex: o.flex,
    fb: o.fb,
    flat: o.flat,
    play: () => melodyPlayer(target, o),
    playSlow: target.length > 1 ? () => melodyPlayer(target, { ...o, bpm: (o.bpm || 92) * 0.6, keyChord: null }) : null,
    maxMiss: o.maxMiss,
    points: o.points,
    reviewLabel: o.review || (target.length === 1 ? 'Tek nota' : `${target.length} notalı melodi`),
    hints: [
      { label: 'İlk nota', run: () => `İlk nota: **${noteName(target[0] as number, { flat: o.flat })}**.` },
      ...(target.length > 1 ? [{ label: 'Yön', run: () => `Melodinin yönü: ${contour(target)}` }] : []),
      { label: 'Sapta göster', run: (api) => api.reveal() },
    ],
  };
}

const POOL_L2 = [40, 41, 43, 45, 47, 48, 50, 52, 53, 55, 57, 59, 60, 62, 64, 65, 67];
const KEYS6: { t: number; m: Mode }[] = [
  { t: 0, m: 'major' },
  { t: 7, m: 'major' },
  { t: 2, m: 'major' },
  { t: 9, m: 'major' },
  { t: 4, m: 'major' },
  { t: 9, m: 'minor' },
  { t: 4, m: 'minor' },
];
const keyChordSym = (t: number, m: Mode) => chordSymbol(t, m === 'minor' ? 'm' : '');
const scaleOf = (m: Mode) => (m === 'minor' ? 'minor' : 'major');

interface GitarLevel extends LevelDef {
  total: number;
  make: (rng: Rng) => NotesQuestion;
}

const lv = (n: number, title: string, desc: string, total: number, make: GitarLevel['make']): GitarLevel => ({
  n,
  title,
  desc,
  total,
  make,
  key: `gitar.egz.${n}`,
  route: `gitar.egz.${n}`,
});

export const LEVELS: GitarLevel[] = [
  lv(1, 'Tek tel, tek nota', '2. telde (Si teli) ilk beş perde. Duyduğun notayı aynı telde bul.', 8, (rng) =>
    notesQ([choice([59, 60, 62, 64], rng)], { flex: false, fb: { from: 0, to: 5 }, info: '2. tel · 0–5. perde arası' }),
  ),
  lv(2, 'Tek nota, ilk pozisyon', 'İlk üç perdedeki doğal notalar. Notayı istediğin telde çalabilirsin.', 10, (rng) =>
    notesQ([choice(POOL_L2, rng)], { fb: { from: 0, to: 5 }, info: 'İlk pozisyon · doğal notalar' }),
  ),
  lv(3, 'İki nota', '{{pc:C}} majör içinde iki notalık adımlar ve küçük atlamalar.', 10, (rng) =>
    notesQ(melody(rng, { tonic: 0, scale: 'major', len: 2, lo: 48, hi: 67, maxLeap: 5 }), {
      info: `${keyName(0, 'major')} · 2 nota`,
    }),
  ),
  lv(
    4,
    'Üç nota · pentatonik',
    '{{pc:G}} majör pentatonik (aynı zamanda {{pc:E}} minör pentatonik) içinden üç notalık motifler.',
    8,
    (rng) =>
      notesQ(melody(rng, { tonic: 7, scale: 'majPent', len: 3, lo: 43, hi: 67, maxLeap: 5 }), {
        info: `${pcName(7)} majör pentatonik · 3 nota`,
        keyChord: 'G',
      }),
  ),
  lv(
    5,
    'Dört nota · majör gam',
    '{{pc:C}} ya da {{pc:G}} majörde dört notalık melodiler. Önce tonaliteyi duyurmak için akor çalınır.',
    8,
    (rng) => {
      const t = choice([0, 7], rng);
      return notesQ(
        melody(rng, { tonic: t, scale: 'major', len: 4, lo: 48, hi: 69, maxLeap: 5, endOnTonic: rng() < 0.5 }),
        { info: `${keyName(t, 'major')} · 4 nota`, keyChord: keyChordSym(t, 'major') },
      );
    },
  ),
  lv(
    6,
    'Beş–altı nota',
    'Farklı tonlarda daha uzun melodiler: {{pc:C}}, {{pc:G}}, {{pc:D}}, {{pc:A}}, {{pc:E}} majör; {{pc:A}} ve {{pc:E}} minör.',
    6,
    (rng) => {
      const k = choice(KEYS6, rng);
      const len = rng() < 0.5 ? 5 : 6;
      return notesQ(
        melody(rng, { tonic: k.t, scale: scaleOf(k.m), len, lo: 45, hi: 72, maxLeap: 7, endOnTonic: rng() < 0.6 }),
        { info: `${keyName(k.t, k.m)} · ${len} nota`, keyChord: keyChordSym(k.t, k.m), flat: keyFlat(k.t, k.m) },
      );
    },
  ),
  lv(
    7,
    'Kromatik dokunuşlar',
    'Gam dışından gelen yaklaşma notaları içeren melodiler. Yarım ses kaymalarını yakala.',
    6,
    (rng) => {
      const k = choice(KEYS6.slice(0, 5), rng);
      const len = 4 + Math.floor(rng() * 3);
      return notesQ(melody(rng, { tonic: k.t, scale: 'major', len, lo: 48, hi: 72, maxLeap: 5, chromatic: true }), {
        info: `${keyName(k.t, 'major')} · ${len} nota · kromatik`,
        keyChord: keyChordSym(k.t, 'major'),
      });
    },
  ),
  lv(8, 'Riff modu', "Kalın tellerde minör pentatonik ve blues riff'leri. Rock ve blues çalanlar için.", 6, (rng) => {
    const t = choice([4, 9], rng);
    const sc = rng() < 0.5 ? 'minPent' : 'blues';
    const len = 4 + Math.floor(rng() * 3);
    return notesQ(
      melody(rng, { tonic: t, scale: sc, len, lo: 40, hi: 57, maxLeap: 5, stepBias: 0.7, allowRepeat: true }),
      {
        info: `${pcName(t)}${sc === 'blues' ? ' blues' : ' minör pentatonik'} · ${len} nota`,
        bpm: 112,
        fb: { from: 0, to: 8 },
      },
    );
  }),
];

export const CHALLENGES: ChallengeDef[] = [
  {
    id: 'hiz',
    title: '60 saniye nota avı',
    desc: 'Bir dakika içinde duyduğun kadar notayı gitarda bul. Her doğru nota bir puan.',
    badge: '60 sn',
    input: 'notes',
    key: 'gitar.meydan.hiz',
    route: 'gitar.meydan.hiz',
    cfg: {
      timeLimit: 60,
      autoAdvance: true,
      starAt: [8, 14, 20],
      rules: [
        'İlk pozisyondaki doğal notalar çalınır.',
        'Doğru notayı çaldığın anda sıradaki gelir.',
        'Üç yanlış denemede soru geçilir.',
      ],
      make: (_i, _st, rng) => notesQ([choice(POOL_L2, rng)], { maxMiss: 3, info: 'İlk pozisyon' }),
      onFinish: (st) => {
        if (st.score >= 20) award('hiz_20');
      },
    },
  },
  {
    id: 'hayatta',
    title: 'Hayatta kal',
    desc: 'Melodiler her iki turda bir nota uzar. Üç canın var; bir melodide üç yanlış nota bir can götürür.',
    badge: '3 can',
    input: 'notes',
    key: 'gitar.meydan.hayatta',
    route: 'gitar.meydan.hayatta',
    cfg: {
      lives: 3,
      autoAdvance: true,
      scoreLabel: 'Toplam nota',
      rules: ['İki notayla başlar, en fazla sekiz notaya kadar uzar.', 'Puan, çözdüğün melodilerin nota sayısıdır.'],
      make: (_i, st, rng) => {
        const len = Math.min(8, 2 + Math.floor(st.answered / 2));
        const k = choice(KEYS6, rng);
        return notesQ(melody(rng, { tonic: k.t, scale: scaleOf(k.m), len, lo: 45, hi: 72, maxLeap: 7 }), {
          maxMiss: 3,
          points: len,
          info: `${keyName(k.t, k.m)} · ${len} nota`,
          keyChord: keyChordSym(k.t, k.m),
          flat: keyFlat(k.t, k.m),
        });
      },
    },
  },
  {
    id: 'gunun',
    title: 'Günün melodileri',
    desc: 'Herkes için aynı beş melodi, her gün yenilenir. Bugünün rekorunu kır.',
    badge: 'Günlük',
    input: 'notes',
    key: 'gitar.meydan.gunun',
    route: 'gitar.meydan.gunun',
    cfg: {
      total: 5,
      scoreLabel: 'Puan',
      rules: ['Melodiler 3 ile 6 nota arasında.', 'Bir melodide dört yanlış denemede o melodi geçilir.'],
      seeded: true,
      make: (i, _st, rng) => {
        const lens = [3, 4, 4, 5, 6];
        const k = KEYS6[Math.floor(rng() * KEYS6.length)] as (typeof KEYS6)[number];
        const len = lens[i] || 5;
        return notesQ(
          melody(rng, { tonic: k.t, scale: scaleOf(k.m), len, lo: 45, hi: 72, maxLeap: 7, endOnTonic: true }),
          {
            maxMiss: 4,
            points: len,
            info: `${i + 1}. melodi · ${keyName(k.t, k.m)}`,
            keyChord: keyChordSym(k.t, k.m),
            flat: keyFlat(k.t, k.m),
          },
        );
      },
    },
  },
];

export function levelConfig(level: GitarLevel): ExerciseConfig {
  const rng: Rng = Math.random;
  const next = LEVELS[LEVELS.indexOf(level) + 1];
  return {
    key: level.key,
    module: 'gitar',
    back: 'gitar.antrenman',
    title: level.title,
    levelNo: level.n,
    desc: level.desc,
    total: level.total,
    input: 'notes',
    next: next ? next.route : null,
    rules: [
      'Uygulama notaları çalar; sen aynı notaları gitarında çalarsın.',
      'Mikrofon her notayı dinler. Yanlış notada hedefin daha ince mi kalın mı olduğunu söyler.',
      getSettings().octaveMode === 'flex'
        ? "Oktav esnek: notanın adı doğruysa farklı oktavda çalman da kabul edilir (Ayarlar'dan değiştirebilirsin)."
        : 'Oktav kesin: notayı aynı yükseklikte çalmalısın.',
    ],
    make: () => level.make(rng),
  };
}
